import type { StyledComponent } from "@emotion/styled";
import { useSetAtom } from "jotai";
import { useEffect } from "react";

import { appBarAtom } from "scmp-app/components/app-bar/atoms";
import type { AppBarVariant } from "scmp-app/components/app-bar/types";
import { useIntersection } from "scmp-app/lib/hooks";

import { useAppBarStickyHeightValue } from "./sticky";

type Parameter = {
  swapTiming?: "enter" | "exit";
  swapVariant: AppBarVariant;
  tracker?: StyledComponent<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
};

export const useAppBarScrollOverEffect = ({
  swapTiming = "enter",
  swapVariant,
  tracker: Tracker,
}: Parameter) => {
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const setAppBarState = useSetAtom(appBarAtom);

  const { captureIntersectionElement, intersection } = useIntersection({
    rootMargin: `${appBarStickyHeight * -1}px 0px 0px 0px`,
    threshold: [1, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1, 0],
  });

  useEffect(() => {
    const check =
      intersection?.intersectionRatio !== undefined &&
      (swapTiming === "enter"
        ? intersection.intersectionRatio < 1
        : intersection.intersectionRatio === 0);

    setAppBarState({ scrollOverVariant: check ? swapVariant : null });

    return () => {
      setAppBarState({ scrollOverVariant: null });
    };
  }, [intersection?.intersectionRatio, setAppBarState, swapTiming, swapVariant]);

  return {
    captureIntersectionElement,
    trackerNode: Tracker ? <Tracker ref={captureIntersectionElement} /> : null,
  };
};
